/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Typed accessors for the ATP (Available-to-Promise) custom fields the
 * `int_sfnext_atp` cartridge injects into the SCAPI basket response.
 *
 * The cartridge enriches the `getBasket` response in a
 * `dw.ocapi.shop.basket.modifyGETResponse` hook, writing `c_atp*` fields onto
 * each product item and a small set of aggregate fields onto the basket root.
 * SCAPI types these as loosely-typed custom properties, so this module narrows
 * them into stable shapes the storefront can consume without scattering
 * `as Record<string, unknown>` casts across components.
 *
 * Field names mirror `AtpEnrichmentHelper.injectAtpIntoResponse`.
 */

import { getMerchandiseProductItems } from '@/lib/extend-basket-utils';

/** Basket-level `c_atpStatus` flag set by the enrichment hook. */
export type AtpStatus = 'enriched' | 'no_zip' | 'error';

/** Per-line-item ATP availability, narrowed from the SCAPI custom properties. */
export interface AtpItemFields {
    /** Best (earliest) promised delivery date, ISO `YYYY-MM-DD`. */
    bestDate: string | null;
    /** Backorder range start, ISO `YYYY-MM-DD`. */
    beginDateRange: string | null;
    /** Backorder range end, ISO `YYYY-MM-DD`. */
    endDateRange: string | null;
    /**
     * Whether the item is available. `null` means availability is unknown
     * (e.g. 3PBB partner-fulfilled items ATP can't see) and must not be
     * treated as unavailable.
     */
    isAvailable: boolean | null;
    /** Whether the promised date qualifies for "Get It By" messaging. */
    isGetItBy: boolean;
    /** Whether the item is on backorder. */
    isOnBackOrder: boolean;
    /** Whether the item was quoted through both HD and DS channels. */
    isMixedMode: boolean;
    /** Whether the item is third-party-fulfilled (3PBB) and bypassed ATP. */
    is3PBB: boolean;
}

/** Basket-level ATP aggregates, narrowed from the SCAPI custom properties. */
export interface AtpBasketFields {
    status: AtpStatus | null;
    allAvailable: boolean | null;
    earliestHDDate: string | null;
    earliestDSDate: string | null;
    latestHDDate: string | null;
    latestDSDate: string | null;
}

function asString(value: unknown): string | null {
    return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * Coerce a SCAPI custom boolean. The hook writes real booleans, but treats an
 * absent value as "unknown" (`null`) rather than `false` so callers can
 * distinguish "ATP says unavailable" from "ATP had nothing to say".
 */
function asBooleanOrNull(value: unknown): boolean | null {
    if (typeof value === 'boolean') return value;
    return null;
}

function asBoolean(value: unknown): boolean {
    return value === true;
}

/**
 * Read the per-line-item ATP fields off a SCAPI product item.
 *
 * @param item - A basket product item (or anything carrying `c_atp*` props).
 */
export function getAtpItemFields(item: unknown): AtpItemFields {
    const record = (item ?? {}) as Record<string, unknown>;
    return {
        bestDate: asString(record.c_atpBestDate),
        beginDateRange: asString(record.c_atpBeginDateRange),
        endDateRange: asString(record.c_atpEndDateRange),
        isAvailable: asBooleanOrNull(record.c_atpIsAvailable),
        isGetItBy: asBoolean(record.c_atpIsGetItBy),
        isOnBackOrder: asBoolean(record.c_atpIsOnBackOrder),
        isMixedMode: asBoolean(record.c_atpIsMixedMode),
        is3PBB: asBoolean(record.c_atpIs3PBB),
    };
}

/** Per-line availability snapshot keyed by product/item id (CHECKOUT-07 inventory diff). */
export interface ProductItemAvailability {
    /** Product id (SKU) — the stable key across a re-priced basket. */
    sku: string | null;
    /** Basket line item id. */
    itemId: string | null;
    /** `c_atpIsAvailable`: `false` = unavailable, `true`/`null` = available/unknown. */
    isAvailable: boolean | null;
}

/**
 * Map a SCAPI basket's product items to a per-line availability snapshot.
 *
 * Used by the CHECKOUT-07 ZIP-change verdict to diff item availability between
 * the prior-ZIP basket (ATP-enriched for the old zone) and a freshly-fetched
 * basket (ATP-enriched for the new zone) — an item flipping available→`false`
 * is an `inventoryShortfall`.
 *
 * Returns EVERY product item including protection-plan / warranty PLIs. Use
 * {@link getMerchandiseAvailabilityLines} for checkout-blocking gates, which
 * must skip warranty lines (an Extend warranty carries no physical inventory
 * — ATP correctly returns `isAvailable=false` for warranty SKUs like
 * `FURNID-5Y`, but that must NEVER block the merchandise from being
 * purchased, and the cart-page CTA gate never does).
 *
 * @param basket - A SCAPI basket response carrying `productItems` with `c_atp*` props.
 */
export function getBasketAvailabilityLines(basket: unknown): ProductItemAvailability[] {
    const items = (basket as { productItems?: unknown[] })?.productItems;
    if (!Array.isArray(items)) return [];
    return items.map((item) => {
        const record = (item ?? {}) as Record<string, unknown>;
        return {
            sku: asString(record.productId),
            itemId: asString(record.itemId),
            isAvailable: getAtpItemFields(item).isAvailable,
        };
    });
}

/**
 * Merchandise-only variant of {@link getBasketAvailabilityLines} — skips
 * protection-plan / warranty PLIs (identified via `isProtectionPlanItem`,
 * matching the cart-page tile filter and the `OrderSummary` "Proceed to
 * Checkout" CTA gate).
 *
 * Every checkout-blocking ATP gate — the checkout loader guard, the
 * shipping-address first-ZIP inventory verdict, the place-order ATP fault
 * handler — MUST use this helper. Warranty SKUs (Extend `FURNID-*`,
 * `FURMATT-*`, `FURNOD-*`) are insurance policies with no physical inventory
 * and ATP always returns `isAvailable=false` for them; using
 * `getBasketAvailabilityLines` there causes a legitimately available basket
 * to be flagged as an `inventoryShortfall` while the cart page (which
 * already filters merchandise-only) shows every line as fine — a
 * user-visible loop between /cart and /checkout with no obvious "unavailable"
 * badge on any product tile.
 *
 * @param basket - A SCAPI basket response carrying `productItems`.
 */
export function getMerchandiseAvailabilityLines(basket: unknown): ProductItemAvailability[] {
    const items = (basket as { productItems?: unknown[] })?.productItems;
    if (!Array.isArray(items)) return [];
    const merchandise = getMerchandiseProductItems(items as Parameters<typeof getMerchandiseProductItems>[0]);
    return merchandise.map((item) => {
        const record = (item ?? {}) as Record<string, unknown>;
        return {
            sku: asString(record.productId),
            itemId: asString(record.itemId),
            isAvailable: getAtpItemFields(item).isAvailable,
        };
    });
}

/**
 * Read the basket-level ATP aggregate fields off a SCAPI basket.
 *
 * @param basket - A basket response (or anything carrying `c_atp*` props).
 */
export function getAtpBasketFields(basket: unknown): AtpBasketFields {
    const record = (basket ?? {}) as Record<string, unknown>;
    const rawStatus = asString(record.c_atpStatus);
    const status =
        rawStatus === 'enriched' || rawStatus === 'no_zip' || rawStatus === 'error' ? (rawStatus as AtpStatus) : null;
    return {
        status,
        allAvailable: asBooleanOrNull(record.c_atpAllAvailable),
        earliestHDDate: asString(record.c_atpEarliestHDDate),
        earliestDSDate: asString(record.c_atpEarliestDSDate),
        latestHDDate: asString(record.c_atpLatestHDDate),
        latestDSDate: asString(record.c_atpLatestDSDate),
    };
}
